import { Controller } from "react-hook-form";
import Multiselect from "multiselect-react-dropdown";
import LoadingSpinner from "../../../components/LoadingSpinner";
import {
  positions,
  sports,
  states,
  tshirtSizes,
} from "../../../utils/PositionStateData";
import { divisions } from "../../../utils/PositionStateData";
import HighSchoolAutocomplete from "./HighSchoolAutocomplete";

const SpotRegisterAthleteStep2 = ({
  register,
  errors,
  watch,
  control,
  setValue,
  getValues,
  clearErrors,
  apiInstance,
  offerFields,
  append,
  remove,
  replace,
  offersReceived,
  isCommitted,
  loading,
  onBack,
}) => {
  const selectedState = watch("state");
  const selectedShirt = watch("tshirtSize");

  return (
    <div className="mb-10">
      {/* Academic Details */}
      <div className="flex items-center gap-3 mt-2 mb-1">
        <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
          Academic Details
        </span>
        <div className="flex-1 h-px bg-gray-500" />
      </div>

      <div className="grid sm:grid-cols-2 gap-x-6">
        <div className="form-field mt-4">
          <label htmlFor="age">Age</label>
          <input
            type="number"
            name="age"
            {...register("age", { required: "Age is required" })}
            placeholder="Age"
            min={11}
            max={30}
            step={1}
          />
          {errors.age && (
            <span className="text-red-500">{errors.age?.message}</span>
          )}
        </div>

        <div className="form-field mt-4">
          <label htmlFor="highSchool">High School</label>
          <Controller
            control={control}
            name="highSchool"
            rules={{ required: "High School is required" }}
            render={({ field: { onChange, onBlur, value } }) => (
              <HighSchoolAutocomplete
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                apiInstance={apiInstance}
                hasError={!!errors.highSchool}
              />
            )}
          />
          {errors.highSchool && (
            <span className="text-red-500">{errors.highSchool?.message}</span>
          )}
        </div>

        <div className="form-field mt-4">
          <label htmlFor="gpa">GPA</label>
          <input
            type="text"
            name="gpa"
            {...register("gpa", { required: "GPA is required" })}
            placeholder="e.g. 0.0"
          />
          {errors.gpa && (
            <span className="text-red-500">{errors.gpa?.message}</span>
          )}
        </div>

        <div className="form-field mt-4">
          <label htmlFor="AcademicInterest">Academic Degree of Interest</label>
          <input
            type="text"
            name="academicDegreeOfInteres"
            {...register("academicDegreeOfInteres", {
              required: "Academic Degree of Interest is required",
            })}
            placeholder="Academic Degree of Interest"
          />
          {errors.academicDegreeOfInteres && (
            <span className="text-red-500">
              {errors.academicDegreeOfInteres?.message}
            </span>
          )}
        </div>
      </div>

      {/* Athletic Information */}
      <div className="flex items-center gap-3 mt-8">
        <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
          Athletic Information
        </span>
        <div className="flex-1 h-px bg-gray-500" />
      </div>

      <div className="grid sm:grid-cols-2 gap-x-6">
        <div className="form-field mt-4">
          <label
            htmlFor="secondaryPosition"
            className="block text-base text-gray-900 dark:text-white"
          >
            Secondary Position
          </label>
          <select
            id="secondaryPosition"
            className={`bg-gray-50 border border-gray-300 text-md rounded-md form-select appearance-none bg-no-repeat ${
              watch("secondaryPosition") ? "text-gray-900" : "text-gray-400"
            }`}
            {...register("secondaryPosition")}
          >
            <option value="" disabled className="text-gray-400">
              None
            </option>
            {positions.map((position) => (
              <option key={position} value={position} className="text-gray-900">
                {position === "Interior Offensive Line"
                  ? "Interior Offensive Line (Guard or Center)"
                  : position}
              </option>
            ))}
          </select>
          {errors.secondaryPosition && (
            <span className="text-red-500">
              {errors.secondaryPosition.message}
            </span>
          )}
        </div>

        <div className="form-field mt-4">
          <label
            htmlFor="sportsParticipation"
            className="block text-base text-gray-900"
          >
            Sports Participation
          </label>
          <Controller
            control={control}
            name="sportsParticipation"
            rules={{ required: "At least one sport is required" }}
            defaultValue={[]}
            render={({ field: { onChange, value } }) => {
              const sportsOptions = sports.map((sport, index) => ({
                id: index,
                name: sport,
              }));

              return (
                <Multiselect
                  options={sportsOptions}
                  displayValue="name"
                  wrapperClass="rounded-md bg-gray-50 text-gray-900"
                  selectedValues={sportsOptions.filter((s) =>
                    value?.includes(s.name),
                  )}
                  onSelect={(selectedList) => {
                    const selected = selectedList.map((item) => item.name);
                    onChange(selected);
                  }}
                  onRemove={(selectedList) => {
                    const selected = selectedList.map((item) => item.name);
                    onChange(selected);
                  }}
                  placeholder="Select Sports"
                  className="bg-gray-50 text-md rounded-md form-select appearance-none bg-no-repeat text-gray-400"
                />
              );
            }}
          />
          {errors.sportsParticipation && (
            <span className="text-red-500">
              {errors.sportsParticipation.message}
            </span>
          )}
        </div>

        <div className="form-field mt-4">
          <label htmlFor="athletePhone">Years of Varsity Experience</label>
          <input
            type="tel"
            id="varsityYears"
            inputMode="numeric"
            placeholder="Years of Varsity Experience"
            maxLength={15}
            {...register("varsityYears", {
              required: "Years of Varsity Experience is required",
            })}
            className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            onInput={(e) => {
              e.target.value = e.target.value.replace(/\D/g, "");
            }}
          />
          {errors.varsityYears && (
            <span className="text-red-500 text-sm">
              {errors.varsityYears?.message}
            </span>
          )}
        </div>
      </div>

      {/* Contact Information */}
      <div className="flex items-center gap-3 my-6 ">
        <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
          Contact Information
        </span>
        <div className="flex-1 h-px bg-gray-500" />
      </div>

      <div className="grid sm:grid-cols-2 gap-x-6">
        <div className="form-field mt-4">
          <label htmlFor="city">City</label>
          <input
            type="text"
            name="city"
            {...register("city", { required: "City is required" })}
            placeholder="City"
          />
          {errors.city && (
            <span className="text-red-500">{errors.city?.message}</span>
          )}
        </div>
        <div className="form-field mt-4">
          <label htmlFor="state">State</label>
          <select
            name="state"
            className={`bg-gray-50 border border-gray-300 text-md rounded-md form-select appearance-none bg-no-repeat ${
              selectedState ? "text-gray-900" : "text-gray-400"
            }`}
            {...register("state", { required: "State is required" })}
            style={{ backgroundColor: "white" }}
          >
            <option value="" className="text-gray-400" disabled>
              Select a State
            </option>
            {states.map((state) => (
              <option className="text-gray-900" key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
          {errors.state && (
            <span className="text-red-500">{errors.state?.message}</span>
          )}
        </div>
      </div>

      {/* Preferences */}
      <div className="flex items-center gap-3 my-6">
        <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
          Preferences
        </span>
        <div className="flex-1 h-px bg-gray-500" />
      </div>

      <div className="grid sm:grid-cols-2 gap-x-6">
        <div className="form-field">
          <label htmlFor="tshirtSize">T-Shirt Size</label>
          <select
            id="tshirtSize"
            {...register("tshirtSize", {
              required: "T-Shirt Size is required",
            })}
            className={`bg-gray-50 border border-gray-300 text-md rounded-md form-select appearance-none bg-no-repeat ${
              selectedShirt ? "text-gray-900" : "text-gray-400"
            }`}
          >
            <option value="" disabled className="text-gray-400">
              Select T-Shirt Size
            </option>

            {tshirtSizes.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          {errors.tshirtSize && (
            <span className="text-red-500">{errors.tshirtSize.message}</span>
          )}
        </div>

        <div className="form-field ">
          <label htmlFor="universityOfInteres">
            Universities You are Most Interested in Playing For
          </label>
          <input
            type="text"
            name="universityOfInteres"
            {...register("universityOfInteres", {
              required: true,
            })}
            placeholder="Universities You are Most Interested in Playing For"
          />
        </div>
      </div>

      {/* Offers */}
      <div className="flex items-center gap-3 my-6">
        <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
          Offers
        </span>
        <div className="flex-1 h-px bg-gray-500" />
      </div>

      <div className="form-field mt-6">
        <label className="block mb-2 font-semibold">
          Have you received any offers?
        </label>

        <div className="flex items-center gap-6">
          {(() => {
            const offersReceivedRegister = register("offersReceived");
            return (
              <>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="yes"
                    className="mr-2"
                    {...offersReceivedRegister}
                    onChange={(e) => {
                      offersReceivedRegister.onChange(e);
                      if (
                        e.target.value === "yes" &&
                        offerFields.length === 0
                      ) {
                        append({ schoolName: "", division: "", offerYear: "" });
                      }
                    }}
                  />
                  <span>Yes</span>
                </label>

                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="no"
                    className="mr-2"
                    {...offersReceivedRegister}
                    onChange={(e) => {
                      offersReceivedRegister.onChange(e);
                      if (e.target.value === "no") {
                        remove(offerFields.map((_, idx) => idx));
                      }
                    }}
                  />
                  <span>No</span>
                </label>
              </>
            );
          })()}
        </div>
      </div>

      {offersReceived === "yes" && (
        <div className="mt-4 space-y-3">
          {offerFields.map((field, index) => (
            <div
              key={field.id}
              className="grid sm:grid-cols-4 gap-3 items-end border border-gray-200 rounded-md p-3"
            >
              <div className="form-field">
                <label className="block mb-1">School</label>
                <input
                  placeholder="School"
                  {...register(`athleteOffers.${index}.schoolName`, {
                    required: "School is required",
                  })}
                />
                {errors.athleteOffers?.[index]?.schoolName && (
                  <span className="text-red-500 text-sm">
                    {errors.athleteOffers[index].schoolName?.message}
                  </span>
                )}
              </div>

              <div className="form-field">
                <label className="block mb-1">Division</label>
                <select
                  defaultValue=""
                  {...register(`athleteOffers.${index}.division`, {
                    required: "Division is required",
                  })}
                  className="bg-gray-50 border border-gray-300 rounded-md w-full"
                >
                  <option value="">Select</option>

                  {divisions.map((divisionItem) => (
                    <option key={divisionItem} value={divisionItem}>
                      {divisionItem}
                    </option>
                  ))}
                </select>
                {errors.athleteOffers?.[index]?.division && (
                  <span className="text-red-500 text-sm">
                    {errors.athleteOffers[index].division?.message}
                  </span>
                )}
              </div>

              <div className="form-field">
                <label className="block mb-1">Year</label>
                <input
                  type="number"
                  placeholder="offer Year"
                  {...register(`athleteOffers.${index}.offerYear`, {
                    required: "Year is required",
                    validate: (v) =>
                      (/^\d{4}$/.test(String(v)) && Number(v) >= 1900) ||
                      "Enter a valid 4-digit year",
                  })}
                />
                {errors.athleteOffers?.[index]?.offerYear && (
                  <span className="text-red-500 text-sm">
                    {errors.athleteOffers[index].offerYear?.message}
                  </span>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="text-red-600 font-bold px-2 py-1 border border-red-400 rounded hover:bg-red-50"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={() =>
              append({ schoolName: "", division: "", offerYear: "" })
            }
            className="mt-2 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white hover:bg-gray-50"
          >
            Add another
          </button>

          <div className="mt-4 pt-3 border-t border-gray-200">
            <p className="block mb-2 font-semibold">
              Have you accepted any of these offers?
            </p>
            <div className="grid sm:grid-cols-2 gap-x-6">
              <div className="form-field">
                <label htmlFor="offerAcceptanceStatus">Offer Status</label>
                <select
                  id="offerAcceptanceStatus"
                  {...register("offerAcceptanceStatus")}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-md rounded-md form-select appearance-none bg-no-repeat"
                >
                  <option value="">No</option>
                  <option value="Committed">
                    I have made a verbal commitment
                  </option>
                  <option value="Signed">
                    I have signed a letter of intent
                  </option>
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="offerAcceptanceSchool">School</label>
                <input
                  type="text"
                  id="offerAcceptanceSchool"
                  placeholder="School name"
                  disabled={!isCommitted}
                  {...register("offerAcceptanceSchool", {
                    validate: (v) => {
                      const status = getValues("offerAcceptanceStatus");
                      return (
                        status === "" ||
                        v?.trim().length > 0 ||
                        "School is required"
                      );
                    },
                  })}
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    !isCommitted
                      ? "opacity-50 cursor-not-allowed bg-gray-100"
                      : ""
                  }`}
                />
                {errors.offerAcceptanceSchool && (
                  <span className="text-red-500 text-sm">
                    {errors.offerAcceptanceSchool.message}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Waiver & Liability Agreement */}
      <div className="flex items-center gap-3 my-6">
        <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
          Waiver & Liability Agreement
        </span>
        <div className="flex-1 h-px bg-gray-500" />
      </div>

      <div className="mt-6 p-3 border rounded-md bg-gray-50">
        <div className="h-40 overflow-y-scroll border p-3 bg-white rounded-md text-sm leading-6">
          <p className="text-sm">
            I hereby acknowledge that I am, or my child is, voluntarily
            participating in a Combine organized by Track It Verified, LLC ("the
            Activity"). In consideration for being permitted to participate in
            this activity, I hereby agree as follows:
          </p>
          <ol className="list-decimal list-inside text-sm mt-2">
            <li className="pb-3">
              {" "}
              I understand and acknowledge that there are certain risks and
              dangers associated with the Activity, including but not limited
              to: getting injured while performing the combine drills.{" "}
            </li>
            <li className="pb-3">
              On behalf of myself (if 18 or older) or on behalf of my child,
              I/we voluntarily assume all risks related to participation in the
              Activity and agree to release and hold harmless Track It Verified,
              LLC, its officers, employees, volunteers, and agents from any and
              all claims, damages, liabilities, costs, and expenses that may
              arise as a result of participation in the Activity, including but
              not limited to injury or loss.{" "}
            </li>
            <li className="pb-3">
              On behalf of myself (if 18 or older) or on behalf of my child,
              I/we agree to indemnify and defend Track It Verified, LLC, its
              officers, employees, volunteers, and agents against any and all
              claims, damages, liabilities, costs, and expenses, including
              attorney fees, arising from or in connection with participation in
              the Activity.{" "}
            </li>
            <li className="pb-3">
              On behalf of myself (if 18 or older) or on behalf of my child,
              I/we certify that I or my child is in good health and have/has no
              physical or mental conditions that prevent me or my child from
              safely participating in the Activity.{" "}
            </li>
            <li className="pb-3">
              On behalf of myself (if 18 or older) or on behalf of my child, I
              grant Track It Verified, LLC the right to use photographs, video
              recordings, or other media of me or my child taken during the
              Activity for promotional or marketing purposes. If I am signing
              this on behalf of my child, I understand that Pursuant to the
              Children’s Online Policy Protection Act (“COPPA”), Track It
              Verified, LLC, located at 1899 Oak Lane, Provo, Utah 84604, wishes
              to collect personal information from my child. The “personal
              information” of the Child includes, images, photographs, video
              recordings, name acknowledgment, video tapings, statistics,
              height, weight and other information pertaining to the Child while
              participating in the Activity. I understand that Parent/Guardian
              consent is required for the collection, use and disclosure of the
              child's personal information. I hereby acknowledge that I am the
              Parent/Guardian of the child listed above and consent to Track It
              Verified, LLC's collection, use, and disclosure of the child's
              personal information by signing this document. I understand that I
              have the option to consent to the collection and use of the
              child's personal information without consenting to disclosure of
              the child's information to third parties. I understand that my
              failure to consent to Track It Verified, LLC's collection use and
              disclosure of the child's personal information will not prevent
              the child from participating in the Activity. Track It Verified,
              LLC, will not require a child to disclose more information than is
              reasonably necessary to participate in the Activity as a condition
              of participation. Parent/Guardian can review the child's personal
              information, ask to have it deleted, and refuse to allow any
              further collection use or disclosure of the child's information.
              Track It Verified, LLC has established and maintains reasonable
              procedures to protect the security and integrity of the personal
              information collected from the child. If the Parent/Guardian does
              not wish to provide consent for Track It Verified, LLC's
              collection, use, and disclosure of the child's personal
              information or seeks to revoke previously granted consent,
              Parent/Guardian must contact Track It Verified, LLC at:
              trackittverified@gmail.com and make this request directly.
              Parent/Guardian releases Track It Verified, LLC, its employees,
              contractors, and each and all persons and parties involved from
              any liability connected with the taking, recording, or publication
              of said photographs, video, or computer images involving the
              Child. Parent/Guardian waives all rights he/she may have to any
              claims for any payment or royalties in connection with any
              exhibition, televising, or other publication of these materials,
              regardless of the purpose or sponsoring of such exhibiting,
              broadcasting, or other publication. All negatives and positives,
              whether prints, digital video, film or video, are the exclusive
              property of Track It Verified, LLC or the person or entity
              designated by it, solely and completely.
            </li>
          </ol>
          <p className="mt-1 text-sm">
            I HAVE READ THIS RELEASE, IF SIGNING MYSELF OR ON BEHALF OF MY
            CHILD, AND FULLY UNDERSTAND ITS TERMS, AND UNDERSTAND THAT I AM
            SIGNING FREELY AND VOLUNTARILY WITHOUT ANY INDUCEMENT.
          </p>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <input
            type="checkbox"
            {...register("agreedToWaiver", { required: true })}
            className="w-4 h-4"
          />
          <label className="text-sm">
            I have read and agree to the Waiver & Liability Agreement.
          </label>
        </div>

        {errors.agreedToWaiver && (
          <p className="text-red-500 text-xs mt-1">
            You must accept the waiver before registering.
          </p>
        )}
      </div>

      <div className="form-field mt-4">{loading && <LoadingSpinner />}</div>
      <div className="flex gap-3 mt-4">
        <button
          type="button"
          onClick={onBack}
          className="border border-gray-400 text-white uppercase rounded px-8 py-2 text-center hover:bg-gray-50 hover:text-gray-700 cursor-pointer w-full"
        >
          Back
        </button>
        <button
          type="submit"
          className="bg-red-500 text-white uppercase rounded px-8 py-2 text-center hover:bg-red-700 cursor-pointer w-full"
        >
          Submit
        </button>
      </div>
    </div>
  );
};

export default SpotRegisterAthleteStep2;
